import React, { useEffect } from "react";
import Joyride, { EVENTS, STATUS } from "react-joyride";
import { useNavigate, useLocation } from "react-router-dom";
import { useJoyrideContext } from "../../contexts/JoyrideContext";
import { useUserContext } from "../../contexts/UserContext";
import { TUTORIAL_TOURS } from "../../config/tourConfig";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import { X } from "lucide-react";
import Button from "../UI/Button";

const getLocalStorageKey = (userId) => `hasSeenSetupTour_${userId}`;

const TourTooltip = ({
    continuous,
    index,
    isLastStep,
    step,
    backProps,
    closeProps,
    primaryProps,
    skipProps,
    tooltipProps,
    size,
}) => {
    return (
        <div
            {...tooltipProps}
            className="w-[360px] max-w-[calc(100vw-32px)] bg-gradient-to-b from-white via-white to-primary-soft/20 rounded-xl shadow-modal border border-border-subtle p-5 text-stride-body z-[10000000] relative overflow-hidden before:content-[''] before:absolute before:inset-x-0 before:top-0 before:h-1 before:bg-gradient-to-r before:from-primary before:via-[#7489d4] before:to-[#9aa8dc]"
        >
            {/* Header: Progress tag & Close */}
            <div className="flex items-center justify-between mb-2.5">
                <span className="text-[11px] font-bold text-primary tracking-wider uppercase bg-primary-soft border border-primary/20 px-2 py-0.5 rounded-md">
                    Step {index + 1} of {size}
                </span>
                <button
                    {...closeProps}
                    type="button"
                    className="p-1 rounded-md text-stride-muted hover:text-primary hover:bg-primary-soft transition-colors"
                    aria-label="Close tour"
                >
                    <X size={15} />
                </button>
            </div>

            {/* Title */}
            {step.title && (
                <h4 className="text-sm font-bold text-stride-heading mb-1.5">
                    {step.title}
                </h4>
            )}

            {/* Content */}
            <div className="text-xs text-stride-muted leading-relaxed mb-4">
                {step.content}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t border-border-subtle/80">
                {skipProps && !isLastStep ? (
                    <button
                        {...skipProps}
                        type="button"
                        className="text-xs font-semibold text-stride-muted hover:text-primary transition-colors"
                    >
                        Skip
                    </button>
                ) : (
                    <div />
                )}

                <div className="flex items-center gap-2">
                    {index > 0 && (
                        <Button
                            variant="secondary"
                            size="xs"
                            {...backProps}
                            type="button"
                        >
                            Back
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        size="xs"
                        {...primaryProps}
                        type="button"
                    >
                        {isLastStep ? "Finish" : "Next"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const AppJoyride = ({ isAuthenticated }) => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();
    const { state, setState, endTour, startTour } = useJoyrideContext();
    const navigate = useNavigate();
    const location = useLocation();

    const pathSegments = location.pathname.split("/").filter(Boolean);
    const routeId = pathSegments[pathSegments.length - 1] || null;

    const waitForElement = (selector, timeout = 6000) =>
        new Promise((resolve, reject) => {
            const interval = 100;
            let elapsedTime = 0;
            const checkExistence = () => {
                const element = document.querySelector(selector);
                if (element) {
                    resolve(element);
                } else if (elapsedTime >= timeout) {
                    reject(
                        new Error(
                            `Element with selector "${selector}" not found within ${timeout}ms`
                        )
                    );
                } else {
                    elapsedTime += interval;
                    setTimeout(checkExistence, interval);
                }
            };
            checkExistence();
        });

    const isNewUser = () => {
        if (process.env.REACT_APP_TEST_USER_EMAIL === userData.email) {
            return false;
        }
        return !userData?.defaultAdobeIO?._id || userData.logins_count <= 1;
    };

    const getUserRole = () => {
        const defaultOrgId = userData?.user_metadata?.default_organization;
        const defaultOrg = userData?.organizations?.find(
            (org) => org.name === defaultOrgId && !org.isBlocked
        );
        return defaultOrg?.role?.[0]?.name?.toLowerCase() || "";
    };

    const getTourSteps = () => {
        if (isNewUser()) {
            const hasSeenTour =
                localStorage.getItem(getLocalStorageKey(userData?.user_id)) ===
                "true";
            if (hasSeenTour) return {}; // prevent re-running after refresh

            const orgLevel = userData?.selectedOrganization?.level.id;
            const userRole = getUserRole();
            if (
                orgLevel === "3" &&
                (userRole === "owner" || userRole === "admin")
            ) {
                const allSteps = [
                    ...TUTORIAL_TOURS(content.TOURS).SETUP_TOUR.steps,
                ];
                return {
                    currentTour: TUTORIAL_TOURS(content.TOURS).SETUP_TOUR.id,
                    steps: allSteps,
                };
            }

            return {};
        }
        return {};
    };

    useEffect(() => {
        if (isAuthenticated && userData) {
            const { steps, currentTour } = getTourSteps();
            if (steps && steps?.length > 0) {
                setTimeout(() => {
                    localStorage.setItem(
                        getLocalStorageKey(userData?.user_id),
                        "true"
                    );
                    setState((prev) => ({ ...prev, steps }));
                    startTour(currentTour);
                }, 700);
            }
        }
    }, [isAuthenticated, setState, startTour]);

    const handleJoyrideCallback = async (data) => {
        const {
            action,
            index,
            status,
            type,
            step: {
                data: { next, previous, isClickRequiredOnSpotlight } = {},
                target,
            } = {},
        } = data;

        // Helper function to check if target is within ag-grid
        const isAgGridElement = (selector) => {
            const element = document.querySelector(selector);
            return element?.closest(".ag-theme-quartz") !== null;
        };

        // Helper function to check if the checkbox condition is met
        const isCheckboxConditionMet = () => {
            const checkbox = document.querySelector(
                '#source-wrapper-adobe-target input[type="checkbox"]'
            );
            return checkbox?.checked ?? false;
        };

        // Handle tour completion or skipping
        if (
            [STATUS.FINISHED, STATUS.SKIPPED].includes(status) ||
            action === "close"
        ) {
            if (state?.currentTour === "AI_ASSISTANT_TOUR") {
                const dropdown = document.getElementById("ai-connectors-dropdown");
                if (dropdown) {
                    const trigger = document.getElementById("ai-connectors-trigger");
                    if (trigger) trigger.click();
                }
                const closeBtn = document.getElementById("ai-close-btn");
                if (closeBtn) closeBtn.click();
            }
            endTour();
            return;
        }

        if (type === EVENTS.STEP_AFTER) {
            const nextStepIndex = action === "prev" ? index - 1 : index + 1;

            // Ensure the step index is within bounds
            if (nextStepIndex < state.steps.length && nextStepIndex >= 0) {
                const nextStep = state.steps[nextStepIndex];

                // Handle checkbox condition for a specific step
                if (
                    state?.currentTour == "SETUP_TOUR" &&
                    nextStepIndex === 2 &&
                    action === "next" &&
                    !isCheckboxConditionMet()
                ) {
                    // If the checkbox isn't checked, stay on the current step
                    setState((prevState) => ({
                        ...prevState,
                        stepIndex: nextStepIndex - 1,
                    }));
                    return;
                }

                // Navigation logic based on `next` and `previous` data
                try {
                    // Hide Joyride before navigation
                    setState((prev) => ({ ...prev, run: false }));
                    let modifiedPrevious = previous;
                    if (
                        modifiedPrevious &&
                        modifiedPrevious?.includes("routeId")
                    ) {
                        modifiedPrevious = modifiedPrevious.replace(
                            "routeId",
                            routeId
                        );
                    }
                    // Handle navigation if needed
                    if (
                        (action === "prev" &&
                            modifiedPrevious &&
                            modifiedPrevious !== location.pathname) ||
                        (action !== "prev" &&
                            next &&
                            next !== location.pathname)
                    ) {
                        navigate(action === "prev" ? modifiedPrevious : next);
                    }

                    if (isClickRequiredOnSpotlight && action !== "prev") {
                        const element = document.getElementById(
                            target.replace("#", "")
                        );
                        if (element) {
                            // Wait for any pending re-renders or transitions
                            await new Promise((res) => setTimeout(res, 350));

                            // Double-check element still exists after delay
                            const freshElement = document.getElementById(
                                target.replace("#", "")
                            );
                            if (freshElement) {
                                // Run click in next tick to ensure DOM is stable
                                setTimeout(() => freshElement.click(), 0);
                            }
                        }
                    }

                    if (state?.currentTour === "AI_ASSISTANT_TOUR") {
                        if (nextStepIndex >= 1) {
                            const panel = document.querySelector(".sai-panel--open");
                            if (!panel) {
                                const launcher = document.getElementById("ai-assistant-launcher");
                                if (launcher) launcher.click();
                                await new Promise((res) => setTimeout(res, 350));
                            }
                        } else if (nextStepIndex === 0) {
                            const panel = document.querySelector(".sai-panel--open");
                            if (panel) {
                                const closeBtn = document.getElementById("ai-close-btn");
                                if (closeBtn) closeBtn.click();
                                await new Promise((res) => setTimeout(res, 350));
                            }
                        }

                        if (nextStep?.target === '#ai-connector-item-0' || nextStep?.target === '#ai-connector-toggle-0') {
                            const dropdown = document.getElementById("ai-connectors-dropdown");
                            if (!dropdown) {
                                const trigger = document.getElementById("ai-connectors-trigger");
                                if (trigger) trigger.click();
                                await new Promise((res) => setTimeout(res, 250));
                            }
                        } else if (target === '#ai-connector-item-0' || target === '#ai-connector-toggle-0') {
                            const dropdown = document.getElementById("ai-connectors-dropdown");
                            if (dropdown) {
                                const trigger = document.getElementById("ai-connectors-trigger");
                                if (trigger) trigger.click();
                                await new Promise((res) => setTimeout(res, 250));
                            }
                        }
                    }

                    // Wait for the target element to be available
                    await waitForElement(nextStep.target);

                    // Additional wait for ag-grid elements to render if needed
                    if (isAgGridElement(nextStep.target)) {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 1000)
                        );
                    }

                    // Proceed to the next step
                    setState((prevState) => ({
                        ...prevState,
                        stepIndex: nextStepIndex,
                        run: true,
                    })); // Show Joyride
                } catch (error) {
                    console.error("Error waiting for element:", error);
                    endTour(); // End the tour on error
                }
            } else {
                // End the tour if the step index is out of bounds
                // if (isClickRequiredOnSpotlight && action !== "prev") {
                //     const element = document.getElementById(target.replace("#", ""));
                //     if (element) {
                //         await new Promise((res) => setTimeout(res, 200));
                //         element.click();
                //     }
                // }
                console.log("Tour completed");
                endTour();
            }
        }

        if (type === EVENTS.TOUR_START) {
            if (
                state?.currentTour == "EDIT_DRAFT_EXPERIENCE_TOUR" ||
                state?.currentTour == "EDIT_PUBLISHED_EXPERIENCE_TOUR" ||
                state?.currentTour == "ONE_OFF_TASK_TOUR" ||
                state?.currentTour == "RECURRING_TASK_TOUR" ||
                state?.currentTour == "UPGRADE_PLAN_TOUR" ||
                state?.currentTour == "PURCHASE_TOPUP_TOUR" ||
                state?.currentTour == "AI_ASSISTANT_TOUR"
            ) {
                setState((prevState) => ({ ...prevState, run: false }));
                await waitForElement(target);
                setState((prevState) => ({ ...prevState, run: true }));
            }
        }
    };

    useEffect(() => {
        if (state.run) {
            const currentStep = state.steps[state.stepIndex];
            if (currentStep?.target) {
                document.querySelector(currentStep.target)?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }
        }
    }, [state.run, state.stepIndex, state.steps]);

    if (!isAuthenticated || !state.tourActive) return null;

    return (
        <Joyride
            callback={handleJoyrideCallback}
            continuous
            run={state.run}
            stepIndex={state.stepIndex}
            steps={state.steps}
            scrollToFirstStep={false}
            showProgress
            hideCloseButton={false}
            showSkipButton
            tooltipComponent={TourTooltip}
            styles={{
                options: {
                    arrowColor: "#ffffff",
                    backgroundColor: "#ffffff",
                    primaryColor: "#465DAE",
                    zIndex: 10000000,
                },
            }}
        />
    );
};

export default AppJoyride;
